import { GameComponentForGame, GameState, PositionIndex, PossiblyUnknownGameComponent } from "../implementationTypes";
import { GameComponent, GameComponentAnimationData, GameSituationManagerBase } from "../types";
import { getAllComponentChildren } from "./tableUtility";

const validateDestinationData = (
  destinationComponent: GameComponent,
  positionName?: string,
  positionIndex?: PositionIndex,
) => {
  if (!window.componentPool) {
    console.warn("couldn't validate data for placing a component because window.componentPool is not defined");
    return;
  }
  const destinationComponentDefinition = window.componentPool[destinationComponent.name];
  if (destinationComponentDefinition === undefined) {
    console.warn("component name not found in component pool:", destinationComponent.name);
    return;
  }
  if (positionName) {
    const destinationPositionDefinition = destinationComponentDefinition.positions?.[positionName];
    if (destinationPositionDefinition === undefined) {
      console.warn("position name not found in component pool:", positionName, destinationComponentDefinition);
      return;
    }
    if (
      positionIndex &&
      destinationPositionDefinition.coords &&
      destinationPositionDefinition.coords[positionIndex] === undefined
    ) {
      console.warn("position index not found in component pool:", positionIndex, destinationPositionDefinition);
    }
  }
};

export const manipulationMethods = {
  placeComponent: function (
    movingComponent: GameComponent,
    destinationComponent: GameComponent,
    positionName: string,
    positionIndex: PositionIndex,
    multiPositionIndex: number | undefined,
    animationData?: GameComponentAnimationData,
  ) {
    if (!destinationComponent) {
      console.error("placeComponent() called without destinationComponent");
      return;
    }
    if (!movingComponent) {
      console.error("placeComponent() called without movingComponent");
      return;
    }
    validateDestinationData(destinationComponent, positionName, positionIndex);

    if (positionName && !destinationComponent.children[positionName]) {
      destinationComponent.children[positionName] = {};
    }
    let positions = destinationComponent.children[positionName];
    movingComponent.parent = destinationComponent.id;
    if (animationData) {
      animationData.timestamp = Date.now();
      movingComponent.animationData = animationData;
    }

    if (window.componentPool) {
      if (
        window.componentPool[destinationComponent.name]?.positions &&
        window.componentPool[destinationComponent.name]?.positions[positionName]?.multi
      ) {
        if (!positions[positionIndex]) {
          positions[positionIndex] = [];
        }
        if (multiPositionIndex === undefined || multiPositionIndex > (positions[positionIndex] as any[]).length) {
          multiPositionIndex = (positions[positionIndex] as any[]).length;
        }
        if (multiPositionIndex < 0) {
          multiPositionIndex = (positions[positionIndex] as any[]).length - multiPositionIndex;
        }
        movingComponent.childData = { positionName, childPos: positionIndex, i: multiPositionIndex };
        let positionComponents = positions[positionIndex] as any[];
        positionComponents.splice(multiPositionIndex as number, 0, movingComponent);
        for (let c in positionComponents) {
          if (positionComponents[c]) {
            positionComponents[c].childData.i = +c;
          }
        }
      } else {
        positions[positionIndex] = movingComponent;
        movingComponent.childData = { positionName, childPos: positionIndex };
      }
    }
  },
  removeComponent: function (parent: GameComponent, componentId: string) {
    if (parent && parent.children) {
      for (let positionName of Object.keys(parent?.children)) {
        for (let positionIndex of Object.keys(parent.children[positionName])) {
          let position = parent.children[positionName][positionIndex];
          if (!position) {
            continue;
          }
          if (Array.isArray(position)) {
            let arPosition = position as PossiblyUnknownGameComponent[];
            for (let i in arPosition) {
              let p = arPosition[+i];
              if (p.id === componentId) {
                arPosition.splice(+i, 1);
              }
            }
            for (let i in arPosition) {
              if (arPosition[i]?.childData) {
                arPosition[i].childData.i = +i;
              }
            }
          } else {
            if (position.id === componentId) {
              delete parent.children[positionName][positionIndex];
              return;
            }
          }
        }
      }
    }
  },

  moveComponent: function (
    movingComponent: GameComponent,
    originComponent: GameComponentForGame,
    destinationComponent: GameComponent,
    positionName: string,
    positionIndex: PositionIndex,
    multiPositionIndex: number | undefined,
    realTime = false,
    animationData?: GameComponentAnimationData,
  ) {
    this.removeComponent(originComponent, movingComponent.id);

    this.placeComponent(
      movingComponent,
      destinationComponent,
      positionName,
      positionIndex,
      multiPositionIndex,
      animationData,
    );
  },
  moveAll: function (
    originComponent: GameComponent,
    originPositionName: string,
    originPositionIndex: PositionIndex,
    destinationComponent: GameComponent,
    destinationPositionName: string,
    destinationPositionIndex: PositionIndex,
    realTime: boolean,
  ) {
    if (!originComponent.children[originPositionName]) {
      return true;
    }
    let components = originComponent.children[originPositionName][originPositionIndex];
    if (Array.isArray(components)) {
      for (let c of [...components]) {
        this.moveComponent(
          c,
          originComponent,
          destinationComponent,
          destinationPositionName,
          destinationPositionIndex,
          undefined,
          realTime,
        );
      }
    }
    return true;
  },
  setComponentOption: function (
    component: PossiblyUnknownGameComponent,
    optionName: string,
    newValue: string | number | boolean,
  ) {
    if (!component || !optionName) {
      return;
    }
    if (!component.options) {
      component.options = {};
    }
    component.options[optionName] = newValue;
  },
};

export const stateOfGameManager: GameSituationManagerBase = {
  globals: {},
  createCounter: {},
  players: [],
  deckData: {},
  lastActivePlayer: "",
  componentMap: new Map(),
  forceRecalculateIds: true,
  root: {
    options: {},
    id: "initialComponentId",
    name: "",
    children: {},
  },
  getCurrGameState: function () {
    return this.gameState?.current || {};
  },
  getActiveState: function () {
    let result: GameState = this.getCurrGameState();
    while (result.subState) {
      result = result.subState;
    }
    return result;
  },
  getClosestState: function (stateName: string) {
    let currState: GameState = this.getCurrGameState();
    if (currState.name === stateName) {
      return currState;
    }
    while (currState.subState) {
      currState = currState.subState;
      if (currState.name === stateName) {
        return currState;
      }
    }
    return undefined;
  },
  addSubstate: function (subState) {
    this.getActiveState().subState = subState;
    this.lastActivePlayer = subState.activePlayer ?? "";
    //activatedPlayer(subState.activePlayer);
  },
  setGameState: function (newState) {
    if (typeof newState.stateId !== "string") {
      newState.stateId = newState.name + Date.now().toString(36);
    }
    if (typeof newState.stateVersion !== "number") {
      newState.stateVersion = 0;
    }
    this.gameState.current = newState;
    this.lastActivePlayer = newState.activePlayer ?? "";
    //activatedPlayer(newState.activePlayer);
  },
  getGlobal: function (name) {
    if (this.globals) {
      return this.globals[name];
    }
    return undefined;
  },
  setGlobal: function (name, value) {
    if (this.globals) {
      this.globals[name] = value;
    }
  },
  resolveCurrState: function () {
    let parent = this.getCurrGameState();
    let child = parent.subState;
    while (child?.subState) {
      parent = child;
      child = child.subState;
    }
    this.lastActivePlayer = parent.activePlayer ?? "";
    delete parent.subState;
  },
  getPlayer: function (color) {
    return this.players.find((p) => p.color === color);
  },
  getActivePlayer: function (color) {
    return this.getPlayer(this.getActiveState().activePlayer ?? "");
  },
  getGameComponentById: function (id) {
    if (id === undefined) {
      return;
    }
    if (this.forceRecalculateIds || !this.componentMap.get) {
      let newMap = new Map();
      let allComponents = [];
      if (this.players) {
        for (let p of this.players) {
          allComponents.push(...getAllComponentChildren(p.area));
        }
      }
      allComponents.push(...getAllComponentChildren(this.root));
      for (let c of allComponents) {
        if (c.id === id) {
          return c;
        }
      }
      for (let c of allComponents) {
        newMap.set(c.id, c);
      }
      this.componentMap = newMap;
    }
    this.forceRecalculateIds = false;
    return this.componentMap.get(id);
  },
  getRootComponent: function (componentData) {
    let currComponent = componentData as GameComponentForGame;
    while (currComponent.parent) {
      currComponent = this.getGameComponentById(currComponent.parent) as GameComponentForGame;
    }
    return currComponent;
  },
  getCardData: function (card, rev = false) {
    if (!card?.options) {
      return undefined;
    }
    let deckData = this.deckData[card?.options[rev ? "revDeckName" : "deckName"]];
    if (!deckData) {
      return undefined;
    }
    let cardData = deckData.cards[card?.options[rev ? "revCardId" : "cardId"]];
    if (!cardData) {
      cardData = deckData.cards[card?.options.id]; // bw compatiblity
    }
    return cardData;
  },
  isPlayerActive: function (color: string) {
    let activePlayer = this.getActiveState().activePlayer;
    if (
      color === "alwaysactive" ||
      activePlayer === "everyone" ||
      (Array.isArray(activePlayer) && activePlayer.includes(color))
    ) {
      return true;
    }
    return color === this.getActiveState().activePlayer;
  },
  gameState: {
    current: {
      message: "lol default state",
      activePlayer: undefined,
      buttons: undefined,
      subState: undefined,
      componentOverdrive: {},
    },
  },

  // Following properties will be filled on initialization
  /*options: {
    playerColors: []
  },
  componentOverdrive: {},
  containers: {},
  players: [],
  globals: {},
  deckData: {},
  dataSheet: {},
  ended: false,
  stats: undefined,
  playerStats: {},
  tableStats: {},
  objectStats: {}*/
};
